import React, { ForwardedRef } from 'react'
import classNames from 'classnames'

export type TooltipPosition = 'top' | 'bottom'

export interface TooltipProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>, React.PropsWithChildren {
  /**
   * The content of the Tooltip.
   *
   * @required
   * @type string
   */
  content: string
  /**
   * Whether the Tooltip should always be visible or invisible.
   *
   * @optional
   * @type boolean
   * @default undefined
   */
  forceVisible?: boolean
  /**
   * Whether or not tapping (or clicking) should show/hide the Tooltip
   *
   * @optional
   * @type boolean
   * @default false
   */
  isTappable?: boolean
  /**
   * Preferred vertical position for the Tooltip. When provided, the Tooltip
   * will render in this position instead of auto-flipping.
   *
   * @optional
   * @type 'top' | 'bottom'
   * @default undefined (auto)
   */
  preferredPosition?: TooltipPosition
}

function mergeRefs<T extends HTMLDivElement>(...refs: (React.Ref<T> | undefined)[]) {
  return (value: T) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') ref(value)
      else if (ref && typeof ref === 'object') (ref as React.MutableRefObject<T | null>).current = value
    })
  }
}

const getTooltipStyle = (containerRect: DOMRect, tooltipWidth: number): React.CSSProperties => {
  const offsetRight = containerRect.right + tooltipWidth / 2 > window.innerWidth
  const offsetLeft = containerRect.left - tooltipWidth / 2 < 0

  if (offsetRight) {
    return { right: 0, left: 'auto', transform: 'translateX(0%)' }
  }
  if (offsetLeft) {
    return { left: 0, transform: 'translateX(0%)' }
  }
  return { left: '50%', transform: 'translateX(-50%)' }
}

const getTooltipPosition = (
  containerRect: DOMRect,
  tooltipHeight: number,
  currentPosition: TooltipPosition,
): TooltipPosition => {
  const offsetTop = containerRect.top - tooltipHeight < 0
  if (offsetTop && currentPosition === 'top') return 'bottom'
  if (!offsetTop && currentPosition === 'bottom') return 'top'
  return currentPosition
}

/**
 * The Tooltip component
 * @param {TooltipProps} props - The props for the component
 * @param {React.RefM<HTMLDivElement>} ref - The ref to the root div element
 * @returns {JSX.Element}
 */
export const Tooltip: React.FC<TooltipProps> = React.forwardRef(
  (props: TooltipProps, ref: ForwardedRef<HTMLDivElement>) => {
    // TODO: Make 'id' required in order to facilitate aria-describedby={id} on the triggering element
    // Older uses need to be retrofitted, however
    const {
      children,
      className,
      content,
      forceVisible = undefined,
      id = 'tooltipContent',
      isTappable = false,
      preferredPosition,
    } = props

    const [isVisible, setIsVisible] = React.useState(forceVisible ?? false)
    const [position, setPosition] = React.useState<'top' | 'bottom'>(preferredPosition ?? 'top')
    const tooltipRef = React.useRef<HTMLDivElement | null>(null)
    const containerRef = React.useRef<HTMLDivElement | null>(null)

    React.useEffect(() => {
      if ((isVisible || forceVisible) && tooltipRef.current && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect()
        const tooltipWidth = tooltipRef.current.offsetWidth
        const tooltipHeight = tooltipRef.current.offsetHeight

        const style = getTooltipStyle(containerRect, tooltipWidth)

        if (preferredPosition) {
          setPosition(preferredPosition)
        } else {
          const newPosition = getTooltipPosition(containerRect, tooltipHeight, position)
          setPosition(newPosition)
        }
        Object.assign(tooltipRef.current.style, style)
      }
    }, [forceVisible, isVisible, position, preferredPosition])

    const showTooltip = () => {
      if (forceVisible === undefined) {
        setIsVisible(true)
      }
    }

    const hideTooltip = () => {
      if (forceVisible === undefined) {
        setIsVisible(false)
      }
    }

    const toggleIsVisible = () => {
      if (isTappable) setIsVisible((prev) => !prev)
    }

    if (!content) {
      return children
    }

    return (
      <div
        ref={mergeRefs(containerRef, ref)}
        onBlur={hideTooltip}
        onFocus={showTooltip}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        className="relative"
        onClick={toggleIsVisible}
        tabIndex={0}
      >
        {children}
        {(isVisible || forceVisible) && (
          <div
            id={id}
            ref={tooltipRef}
            className={classNames([
              `absolute z-10 w-max max-w-xs rounded-lg bg-core-gray-950 p-2 text-center text-sm text-white shadow-md ${
                position === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
              }`,
              className,
            ])}
            role="tooltip"
          >
            {content}
          </div>
        )}
      </div>
    )
  },
)

Tooltip.displayName = 'Tooltip'
